import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiMail,
  FiPhone,
  FiGlobe,
  FiMapPin,
  FiUsers,
  FiBriefcase,
  FiEdit,
  FiArrowLeft,
  FiDatabase
} from 'react-icons/fi';
import { getCompanyByID, getLoginLogsByCompany } from '../../services/api/SuperAdminApi';
import { useLoading } from '../../context/LoadingContext';
import './CompanyDetailsPage.scss';
import { BsCurrencyRupee } from "react-icons/bs";
import { SystemInvoices } from '../SystemInvoices/SystemInvoices';



const CompanyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');
  const [loginLogs, setLoginLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchCompanyData = useCallback(() => {
    if (id) {
      showLoading('Loading company details...');
      setError(null);
      getCompanyByID(id)
        .then((res) => {
          console.log('Complete Company data', JSON.stringify(res.data, null, 2));
          // Store the complete response data (both company and planData)
          setCompanyData(res.data);
          hideLoading();
        })
        .catch((error) => {
          console.error('Error fetching company details:', error);
          setError('Failed to load company details');
          hideLoading();
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // const fetchModuleRights = useCallback(() => {
  //   if (id) {
  //     getAllRightsEdit(id)
  //       .then((res) => {
  //         console.log('Module Rights:', res.data);
  //         setModuleRights(res.data);
  //       })
  //       .catch((error) => {
  //         console.error('Error fetching module rights:', error);
  //         // Set empty object if API fails, so it doesn't break the UI
  //         setModuleRights({});
  //       });
  //   }
  // }, [id]);

  useEffect(() => {
    fetchCompanyData();
    //fetchModuleRights();
  }, [fetchCompanyData,]);

  const fetchLoginLogs = useCallback(() => {
    if (id) {
      setLogsLoading(true);
      getLoginLogsByCompany(id)
        .then((res) => {
          console.log('Login Logs:', res.data);
          setLoginLogs(res.data || []);
          setLogsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching login logs:', error);
          setLoginLogs([]);
          setLogsLoading(false);
        });
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'loginLogs') {
      fetchLoginLogs();
    }
  }, [activeTab, fetchLoginLogs]);

  const formatLocation = (company) => {
    const parts = [];
    if (company.address) parts.push(company.address);
    if (company.state) parts.push(company.state);
    if (company.country) parts.push(company.country);
    if (company.zip) parts.push(company.zip);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  const getCompanyInitials = (name) => {
    if (!name) return 'C';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleEditClick = () => {
    navigate(`/home/superadmin/editcompany/${id}`, {
      state: { fromTab: 'companies' }
    });
  };

  const handleBackClick = () => {
    navigate('/home/superadmin', {
      state: { fromTab: 'companies' }
    });
  };

  if (error || !companyData) {
    return (
      <div className="company-detail-container">
        <div className="error-message">{error || 'Company not found'}</div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'invoices':
        return <SystemInvoices companyId={id} />;
      case 'loginLogs':
        return (
          <div className="login-logs-section">
            {logsLoading ? (
              <div className="logs-loading">Loading login logs...</div>
            ) : (
              <div className="logs-table-container">
                {loginLogs.length === 0 ? (
                  <div className="no-data-message">
                    <p>No login logs found for this company</p>
                  </div>
                ) : (
                  <table className="logs-table">
                    <thead>
                      <tr>
                        <th>IP Address</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Created At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginLogs.map((log) => (
                        <tr key={log._id}>
                          <td>{log.ip}</td>
                          <td>
                            <span className={`log-type ${log.type}`}>
                              {log.type}
                            </span>
                          </td>
                          <td>
                            <span className={`log-status ${log.status}`}>
                              {log.status}
                            </span>
                          </td>
                          <td>
                            {new Date(log.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })} {new Date(log.createdAt).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="company-detail-container">
      <div className="company-card">
        {/* Header */}
        <div className="company-header">
          <div className="avatar">
            {getCompanyInitials(companyData?.company?.companyName)}
          </div>
          <div className="company-title">
            <h2>{companyData.company?.companyName}</h2>
            <span className={`company-status ${companyData?.company?.isActive ? 'active' : 'inactive'}`}>
              {companyData.company?.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Company Details Grid - Always visible */}
        <div className="company-details-grid">
          <div className="detail-item">
            <FiMail className="icon" />
            <div>
              <label>Email</label>
              <p>{companyData.company?.email || 'N/A'}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiPhone className="icon" />
            <div>
              <label>Phone</label>
              <p>{companyData.company?.phno || 'N/A'}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiGlobe className="icon" />
            <div>
              <label>Website</label>
              <p>
                {companyData.company?.website ? (
                  <a href={companyData.company.website} target="_blank" rel="noopener noreferrer">
                    {companyData.company.website}
                  </a>
                ) : 'N/A'}
              </p>
            </div>
          </div>

          <div className="detail-item">
            <FiMapPin className="icon" />
            <div>
              <label>Location</label>
              <p>{formatLocation(companyData.company)}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiUsers className="icon" />
            <div>
              <label>Total Users</label>
              <p>{companyData.company?.users?.length || 0}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiBriefcase className="icon" />
            <div>
              <label>Branches</label>
              <p>{companyData.company?.branches?.length || 0}</p>
            </div>
          </div>

          <div className="detail-item">
            <FiDatabase className="icon" />
            <div>
              <label>Data Scraping</label>
              <p>
                <span className={`role-badge ${companyData.planData?.rights?.scraping ? 'enabled' : 'disabled'}`}>
                  {companyData.planData?.rights?.scraping ? 'Enabled' : 'Disabled'}
                </span>
              </p>
            </div>
          </div>

          <div className="detail-item">
            <BsCurrencyRupee className="icon" />
            <div>
              <label>Payment Status</label>
              <p>
                <span className={`payment-status ${companyData.company?.subscription?.paymentStatus?.toLowerCase().replace(' ', '-') || 'unknown'}`}>
                  {companyData.company?.subscription?.paymentStatus || 'N/A'}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="company-actions">
          <button className="edit-company-btn" onClick={handleEditClick}>
            <FiEdit />
            Edit Company
          </button>
          <button className="back-btn" onClick={handleBackClick}>
            <FiArrowLeft />
            Back to Companies
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="tabs-navigation">
          <button
            className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            Invoices
          </button>
          <button
            className={`tab-btn ${activeTab === 'loginLogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('loginLogs')}
          >
            Login Logs
          </button>
        </div>

        {/* Tab Content */}
        {activeTab && (
          <div className="tab-content">
            {renderTabContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetailsPage;
